import React, { useState, useEffect, useRef } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import './StatusBar.css';

const StatusBar = () => {
  // Get WebSocket context with websocketService
  const { 
    connectionStatus: wsConnectionStatus, 
    isAuthenticated,
    websocketService,
    error: contextError 
  } = useWebSocket();
  
  // State for time in different timezones
  const [times, setTimes] = useState({
    local: new Date().toLocaleTimeString(),
    utc: new Date().toLocaleTimeString('en-US', { timeZone: 'UTC' }),
    est: new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York' }),
    london: new Date().toLocaleTimeString('en-US', { timeZone: 'Europe/London' }),
    tokyo: new Date().toLocaleTimeString('en-US', { timeZone: 'Asia/Tokyo' })
  });

  // State for WebSocket connection status
  const [connectionStatus, setConnectionStatus] = useState(wsConnectionStatus);
  const [authStatus, setAuthStatus] = useState(isAuthenticated ? 'authenticated' : 'unauthenticated');
  const [wsError, setWsError] = useState(contextError);
  
  // Use refs to track current status values to avoid state update loops
  const connectionStatusRef = useRef(connectionStatus);
  const authStatusRef = useRef(authStatus);
  const wsErrorRef = useRef(wsError);
  
  // Update from context error
  useEffect(() => {
    setWsError(contextError);
  }, [contextError]);
  
  // Update the refs when state changes
  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);
  
  useEffect(() => {
    authStatusRef.current = authStatus;
  }, [authStatus]);
  
  useEffect(() => {
    wsErrorRef.current = wsError;
  }, [wsError]);
  
  // Update local state when context values change
  useEffect(() => {
    setConnectionStatus(wsConnectionStatus);
  }, [wsConnectionStatus]);
  
  useEffect(() => {
    setAuthStatus(isAuthenticated ? 'authenticated' : 'unauthenticated');
  }, [isAuthenticated]);

  // Update time every second
  useEffect(() => {
    const updateTimezones = () => {
      const now = new Date();
      setTimes({
        local: now.toLocaleTimeString(),
        utc: now.toLocaleTimeString('en-US', { timeZone: 'UTC' }),
        est: now.toLocaleTimeString('en-US', { timeZone: 'America/New_York' }),
        london: now.toLocaleTimeString('en-US', { timeZone: 'Europe/London' }),
        tokyo: now.toLocaleTimeString('en-US', { timeZone: 'Asia/Tokyo' })
      });
    };
    
    const intervalId = setInterval(updateTimezones, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Update WebSocket connection status - only set up listeners once
  useEffect(() => {
    const handleConnect = () => {
      if (connectionStatusRef.current !== 'connected') {
        setConnectionStatus('connected');
      }
      // Clear any error when connection is established
      if (wsErrorRef.current) {
        setWsError(null);
      }
    };

    const handleDisconnect = () => {
      if (connectionStatusRef.current !== 'disconnected') {
        setConnectionStatus('disconnected');
      }
      
      if (authStatusRef.current !== 'unauthenticated') {
        setAuthStatus('unauthenticated');
      }
    };

    const handleAuthenticated = () => {
      if (authStatusRef.current !== 'authenticated') {
        setAuthStatus('authenticated');
      }
      // Clear any error when authentication succeeds
      if (wsErrorRef.current) {
        setWsError(null);
      }
    };

    const handleAuthError = () => {
      if (authStatusRef.current !== 'auth_failed') {
        setAuthStatus('auth_failed');
      }
    };
    
    const handleError = (error) => {
      // console.log('[StatusBar] WebSocket error:', error);
      // Only handle non-auth related errors here
      if (error && (!error.message || !error.message.includes('authentication'))) {
        setWsError(error.message || 'WebSocket connection error');
      }
    };

    // Subscribe to WebSocket events
    websocketService.on('connect', handleConnect);
    websocketService.on('disconnect', handleDisconnect);
    websocketService.on('authenticated', handleAuthenticated);
    websocketService.on('authentication_error', handleAuthError);
    websocketService.on('error', handleError);

    // Do an initial check of WebSocket status
    const checkStatus = () => {
      const currentState = websocketService.getConnectionState();
      if (connectionStatusRef.current !== currentState) {
        setConnectionStatus(currentState);
      }
      
      const isAuth = websocketService.isAuthenticated();
      if (isAuth && authStatusRef.current !== 'authenticated') {
        setAuthStatus('authenticated');
      } else if (!isAuth && authStatusRef.current === 'authenticated') {
        setAuthStatus('unauthenticated');
      }
    };
    
    // Check status on mount
    checkStatus();

    // Cleanup listeners on unmount
    return () => {
      // console.log('StatusBar component unmounting - cleaning up WebSocket listeners');
      
      websocketService.off('connect', handleConnect);
      websocketService.off('disconnect', handleDisconnect);
      websocketService.off('authenticated', handleAuthenticated);
      websocketService.off('authentication_error', handleAuthError);
      websocketService.off('error', handleError);
      
      // No need to call disconnect() here since StatusBar is just monitoring,
      // not initiating the WebSocket connection
    };
  }, [websocketService]); // Add websocketService to dependencies

  // Memoize status info calculation to prevent unnecessary re-renders
  const getStatusInfo = () => {
    // If there's a WebSocket error, prioritize showing it
    if (wsError) {
      return {
        text: `Error: ${wsError.substring(0, 25)}${wsError.length > 25 ? '...' : ''}`,
        className: 'error',
        tooltip: wsError
      };
    }
    
    if (connectionStatus === 'disconnected') {
      return {
        text: 'Disconnected',
        className: 'disconnected'
      };
    } else if (connectionStatus === 'connecting') {
      return {
        text: 'Connecting',
        className: 'connecting'
      };
    } else if (connectionStatus === 'connected' && authStatus === 'unauthenticated') {
      return {
        text: 'Connected',
        className: 'connected-not-auth'
      };
    } else if (connectionStatus === 'connected' && authStatus === 'authenticated') {
      return {
        text: 'Connected 🔒',
        className: 'authenticated'
      };
    } else if (authStatus === 'auth_failed') {
      return {
        text: 'Authentication Failed',
        className: 'auth-failed'
      };
    }
    
    return {
      text: 'Unknown',
      className: 'unknown'
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="wtp-status-bar">
      {/* WebSocket Status Indicator */}
      <div className="wtp-status-bar-section">
        <div className="wtp-connection-status-indicator">
          <div className="wtp-ws-status-label">
            <span 
              className={`wtp-ws-indicator ${statusInfo.className}`}
              title={statusInfo.tooltip}
            ></span>
            <span 
              className="wtp-status-text"
              title={statusInfo.tooltip}
            >
              {statusInfo.text}
            </span>
          </div>
        </div>
      </div>

      {/* Timezones */}
      <div className="wtp-status-bar-section wtp-timezones">
        <div className="wtp-timezone-item">
          <span className="wtp-label">Local:</span>
          <span className="wtp-time">{times.local}</span>
        </div>
        <div className="wtp-timezone-item">
          <span className="wtp-label">UTC:</span>
          <span className="wtp-time">{times.utc}</span>
        </div>
        <div className="wtp-timezone-item">
          <span className="wtp-label">EST:</span>
          <span className="wtp-time">{times.est}</span>
        </div>
        <div className="wtp-timezone-item">
          <span className="wtp-label">London:</span>
          <span className="wtp-time">{times.london}</span>
        </div>
        <div className="wtp-timezone-item">
          <span className="wtp-label">Tokyo:</span>
          <span className="wtp-time">{times.tokyo}</span>
        </div>
      </div>
    </div>
  );
};

export default StatusBar; 